/**
 * Icons for the filter sheet (owner, 2026-10-29: "more icons, so users see
 * what each option is"): the topics' headings, the rows' labels, the
 * amenity chips and the facts read from the text. lucide-react only, sized
 * to the text line where they are drawn (DESIGN.md: icons never larger).
 */
import {
  AirVent, ArrowLeftRight, ArrowUpDown, Banknote, Bath, BedDouble, Building, Building2, CalendarDays, CalendarX,
  Camera, Car, ConciergeBell, CookingPot, Crown, Droplets, Eye, FileCheck, FileText, FileWarning, Fence, Flame,
  FlameKindling, Footprints, Fuel, Gauge, Globe, GraduationCap, Hammer, House, Images, KeyRound, LandPlot, Landmark, Layers,
  ListChecks, MapPin, PaintRoller, Paintbrush, Palette, Percent, Plug, Repeat, Ruler, Scan, ScrollText, Shapes, Shield, Shirt,
  Snowflake, Sofa, SquareSplitHorizontal, Frame, DoorOpen, SquareParking, Store, Sun, Tent, Thermometer, ThermometerSun, Timer, TreePalm, Trees, Triangle, User,
  UserRound, Users, Wallet, WashingMachine, Waves, Zap, type LucideIcon,
} from "lucide-react";
import type { FactFilter } from "@/lib/text-facts";
import type { ListingKind } from "@/lib/property-kinds";

export type { LucideIcon };

/** The amenity tags (backend services/feature_tags), by id. A tag missing
 *  here is drawn without an icon. */
export const AMENITY_ICON: Record<string, LucideIcon> = {
  smart_home: House, heated_floor: Thermometer, combi: Flame, central_heating: Thermometer, air_conditioner: AirVent,
  generator: Zap, appliances: WashingMachine, gas: Fuel, fireplace: FlameKindling,
  elevator: ArrowUpDown, parking_underground: SquareParking, parking: Car, closed_yard: Fence, security: Shield,
  concierge: ConciergeBell, playground: Shapes, pool: Waves, sauna: ThermometerSun, terrace: Sun, penthouse: Crown,
  leningrad_project: Building2, stalinka: Building2, khrushchyovka: Building2, kyiv_project: Building2,
  minsk_project: Building2, french_project: Building2, german_project: Building2, experimental_project: Building2,
  spa: Droplets, gazebo: Tent, summer_kitchen: CookingPot, duplex: Layers, a_frame: Triangle,
  sea_view: Waves, beach: TreePalm, park_view: Trees, panoramic: Scan,
  new_repair: PaintRoller, designer_repair: Palette, white_frame: Frame,
  studio: DoorOpen, two_bathrooms: Bath, wardrobe_room: Shirt, converted_layout: SquareSplitHorizontal,
  urgent: Timer, developer_credit: Landmark, interest_free_credit: Percent, barter: Repeat, from_owner: UserRound,
  furnished: Sofa,
  family_only: Users, students: GraduationCap, room_share: BedDouble, daily_rent: CalendarDays,
  utilities_included: Plug, deposit_free: Wallet, women_only: User, men_only: User,
};

/** The five types of the kind picker (owner, 2026-10-29): the sheet's
 *  "Əmlak növü" row and the desktop kind pill's popover. A car reads as
 *  a garage and a parking space both. */
export const KIND_ICON: Record<Exclude<ListingKind, "">, LucideIcon> = {
  apartment: Building2, house: House, land: LandPlot, commercial: Store, garage: Car,
};

/** The facts read from the listing's text (lib/text-facts). */
export const FACT_ICON: Record<FactFilter, LucideIcon> = {
  docs: FileCheck, mortgage: Landmark, no_deed_later: FileWarning, owner: UserRound, bright: Sun,
  ready: PaintRoller, needs_work: Hammer, no_deposit: Wallet, no_months_ahead: CalendarX,
};

/** The sheet's topics and rows. */
export const ICON = {
  deal: ArrowLeftRight, place: MapPin, rooms: BedDouble, price: Banknote, area: Ruler, repair: Paintbrush,
  floor: Layers, docs: FileText, deed: FileText, mortgage: Landmark, deedGap: Ruler, takeover: KeyRound,
  features: ListChecks, source: Globe, seller: UserRound, condition: Paintbrush, rentTerms: Wallet,
  review: Gauge, yield: Percent, building: Building2, developer: Building, metro: Footprints, plot: Ruler,
  photos: Camera, photoHas: Images, listing: CalendarDays, viewed: Eye, rentPeriod: CalendarDays, kind: Building2,
  fromText: ScrollText,
} satisfies Record<string, LucideIcon>;
